import { Link } from "@tanstack/react-router";
import { getUserProfile, getAssocWallet } from "../../lib/api";
import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";

/**
 * AddressToAccount - Displays user account information derived from a wallet address or user ID
 *
 * This component fetches account data from the backend (or cache) and renders a clickable
 * card showing the user's avatar, username, and truncated address. Supports caching via
 * window.idCache to minimize redundant API calls.
 *
 * @param {Object} props
 * @param {string} props.address - Wallet address to look up (optional if userId provided)
 * @param {string|number} props.userId - User ID to look up (takes precedence over address)
 * @param {string} props.title1 - Label text shown above user info
 * @param {string} props.title2 - Suffix appended to "Unknown" fallback names
 * @param {string} props.comment - Optional additional text/comment to display
 * @param {string} props.flags - If set, hides the title1 label
 * @param {string} props.posneg - Unused in current implementation
 * @param {function} props.onClick - Callback when the card is clicked
 * @returns {React.ReactElement} Rendered account card element
 */
const AddressToAccount = ({address, flags, title1, title2, comment, posneg, userId, onClick}) => {

    // Use a single state object for all account information.
    const [accountInfo, setAccountInfo] = useState({
        id: 0,
        username: "Unknown holder",
        displayname: "Unknown"
    });

    // Use useMemo to calculate the trimmed address only when the address prop changes.
    const shortAddress = useMemo(() => {
        if (address && typeof address === 'string' && address.length > 10) {
            return `${address.substr(0, 6)}....${address.substr(-4)}`;
        }
        return "0x0000....0000";
    }, [address]);

    // This single useEffect handles all data fetching logic.
    useEffect(() => {
        let isMounted = true;

        const fetchAccountInfo = async () => {
            // Determine the key for fetching/caching. Prioritize userId if available.
            const key = userId && userId !== 'undefined' ? userId : address;

            if (!key || key === 'undefined') {
                // Reset to default if no valid key
                if (isMounted) setAccountInfo({ id: 0, username: "Unknown holder", displayname: "Unknown" });
                return;
            }

            // Ensure global cache exists
            if (!window.idCache) window.idCache = {};

            // 1. Check cache first
            if (window.idCache[key]) {
                if (isMounted) setAccountInfo(window.idCache[key]);
                return;
            }

            // Handle special case for the zero address
            if (key === "0x0000000000000000000000000000000000000000") {
                const nobody = { id: 0, username: `Nobody ${title2 || ''}`, displayname: "Nobody" };
                window.idCache[key] = nobody;
                if (isMounted) setAccountInfo(nobody);
                return;
            }

            // 2. Fetch from backend if not in cache
            try {
                let accountData;
                if (userId) {
                    // Fetch by userId using getUserProfile (returns User object)
                    const profile = await getUserProfile(userId);
                    accountData = profile;
                } else {
                    // Fetch by address using getAssocWallet (returns user/account data)
                    const result = await getAssocWallet({ address });
                    accountData = result; // Should have id, username, etc.
                }

                if (!isMounted) return;

                if (accountData && !accountData.error) {
                    window.idCache[key] = accountData;
                    setAccountInfo(accountData);
                } else {
                    throw new Error(accountData?.message || "User not found");
                }
            } catch (error) {
                if (!isMounted) return;
                // On error, set a default "Unknown" state and cache it to prevent re-fetching.
                console.error(`Failed to fetch account for ${key}:`, error);
                const unknown = { id: 0, username: `Unknown ${title2 || ''}`, displayname: "Unknown" };
                window.idCache[key] = unknown;
                setAccountInfo(unknown);
            }
        };

        fetchAccountInfo();

        // Cleanup function to set isMounted to false
        return () => {
            isMounted = false;
        };
    }, [address, userId, title2]); // Re-run effect if address or userId changes.

    return (
        <div onClick={onClick} className="list-group pixel-card p-2 mb-1">
                    {!flags ? <li className="list-group-item border-0 ps-0 pt-0 text-sm pixel-card">
                <strong className="text-light">{title1}:</strong><br/>
            </li> : null}
            <li className="border-0 pt-0  px-0">
                <div className={"d-flex align-items-lg-start"}>
                    <div className={"d-flex align-items-center"}>
                        <Link to={`/profile/${accountInfo.id || "0"}`} className="avatar v2 me-3">
                            <img src={`https://cdn.stealthisnft.xyz/u/${accountInfo.id || "0"}/profile/avatar.jpg`} alt="-"
                                 className="aspect11 border-radius-lg shadow ppic"/>
                        </Link>
                        <div onClick={(e) => {
                            e.stopPropagation(); // Prevent click from bubbling to the parent container
                            toast("Address: " + address);
                        }} className="d-flex align-items-start flex-column justify-content-center" style={{cursor: 'pointer'}}>
                            <Link to={`/profile/${accountInfo.id || "0"}`}>
                                <h6 className="text-light text-bold author-name">{accountInfo.username.replace(" [object Object]", "")}</h6>
                            </Link>
                            <span className={"text-light"} title={address}><Link to={"/wallet/"+address}>{shortAddress}</Link></span>
                        </div>
                    </div>
                    {comment ? <div className={"p-0 col d-flex align-items-lg-start text-light"}>{comment}</div> : null}
                </div>
            </li>
        </div>
    );
};

export default AddressToAccount;
